import Vue from 'vue';
import { make } from 'vuex-pathify';
import api from '@/services/api';

import {
  defaultEpsgDefinition,
  defaultEpsgName,
  defaultEpsg,
  defaultSRID,
  additionalEpsgs,
} from '@/assets/js/variables';
const state = {
  cursorPositionEpsg: defaultEpsg,
  center: localStorage.getItem('center') ? JSON.parse(localStorage.getItem('center')) : null, // [715090.42, 245483.11]
  zoom: localStorage.getItem('zoom') ? JSON.parse(localStorage.getItem('zoom')) : null, // 17
  defaultExtent: [714359.9470024359, 244955.4801302308, 715762.905096829, 246086.43614509873],
  scale: undefined,
  identifiedFeatures: [],
  coordinates: [],
  reverseGeocoderCoordinates: [],
  coordinatesEpsgs: {
    'EPSG:4326': {
      text: 'EPSG:4326 (GPS)',
      def: '',
      srid: 4326,
    },
    [defaultEpsg]: {
      text: defaultEpsgName,
      def: defaultEpsgDefinition,
      srid: defaultSRID,
    },
    ...Object.fromEntries(
      Object.entries(additionalEpsgs).filter(entry =>
        process.env.VUE_APP_REGISTERED_EPSG.split(',').includes(entry[1].srid.toString())
      )
    ),
  },
  dataBaseEpsg: undefined,
  cacheFilterFeatures: {},
  erroredFeatures: {},
  selectedFeatures: {},
  highlitedFeatures: {},
  layersToBeChanged: new Set(),
  isOpenStreetMapVisible: true,
  isNewCard: false,
  mapPanelDataFetched: false,
  mapRelationDataFetched: false,
  isMapViewChanged: false,
};

const mutations = {
  ...make.mutations(state),
  SET_CENTER: (state, value) => {
    state.center = value;
    localStorage.setItem('center', JSON.stringify(value));
  },
  SET_ZOOM: (state, value) => {
    state.zoom = value;
    localStorage.setItem('zoom', JSON.stringify(value));
  },
  PUSH_CLICKED_COORDINATES: (state, value) => {
    Vue.set(state.coordinates, state.coordinates.length, value);
  },
  DELETE_CLICKED_COORDINATES: (state, value) => {
    Vue.delete(state.coordinates, value);
  },
  SET_COORDINATES_EPSGS: (state, value) => {
    Vue.set(state.coordinatesEpsgs, `EPSG:${value.srid}`, {
      text: value.verbose_name,
      def: value.proj4,
      srid: value.srid,
    });
    state.dataBaseEpsg = {
      text: value.verbose_name,
      def: value.proj4,
      srid: value.srid,
    };
  },
  ADD_SELECTED_FEATURES: (state, value) => {
    const { features, type } = value;
    const changedLayers = Object.keys(features).map(layerId => {
      Vue.set(state[type], layerId, Array.from(new Set([...(state[type][layerId] || []), ...features[layerId]])));
      return layerId;
    });
    state.layersToBeChanged = new Set([...state.layersToBeChanged, ...changedLayers]);
  },
  DELETE_SELECTED_FEATURES: (state, value) => {
    const { features, type } = value;
    const changedLayers = Object.keys(features)
      .filter(layerId => state[type][layerId])
      .map(layerId => {
        if (features[layerId].length === 0) {
          Vue.delete(state[type], layerId);
        } else {
          features[layerId].forEach(layer => {
            Vue.delete(state[type][layerId], state[type][layerId].indexOf(layer));
          });
        }
        return layerId;
      });
    state.layersToBeChanged = new Set([...state.layersToBeChanged, ...changedLayers]);
  },
  SET_CACHE_FILTER_FEATURES: (state, data) => {
    const { layerId, ids } = data;
    Vue.set(state.cacheFilterFeatures, layerId, Object.freeze(ids));
  },
};

const actions = {
  async getDatabaseSrid({ commit }) {
    const r = await api.get('/crs');
    commit('SET_COORDINATES_EPSGS', r.data.data);
  },
  async mapPrint(ctx, payload) {
    return (await api.post('/map_print', payload)).data.data.tid;
  },
  async getCacheLayerIds({ commit }, payload) {
    const { layerId, filters } = payload;
    const r = await api.post(
      `/layers/features_layers/${layerId}/features`,
      {
        features_filter: filters,
      },
      {
        params: {
          ids_only: true,
        },
      }
    );
    commit('SET_CACHE_FILTER_FEATURES', { layerId, ids: r.data.data.ids });
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
